import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowRight, ShieldCheck } from "@phosphor-icons/react";

export default function Pricing() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [user, loading, nav]);

  const pick = async (plan) => {
    setBusy(plan);
    try {
      const r = await http.post("/billing/checkout", {
        plan,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/brand/helmet_transparent.png" alt="WarPaint" className="w-9 h-9 object-contain" />
            <div className="heading font-black tracking-tighter uppercase leading-none">WarPaint DFS</div>
          </Link>
          {user && <div className="text-xs mono text-zinc-500">{user.email}</div>}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="overline mb-3 flex items-center gap-3 justify-center flex-wrap">
            <span>One plan</span>
            <span data-testid="dk-optimized-badge" className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-[#39FF14]/10 border border-[#39FF14]/40 text-[#39FF14] mono text-[10px] tracking-widest">
              <span className="w-1.5 h-1.5 bg-[#39FF14] rounded-full" />
              BUILT FOR DRAFTKINGS
            </span>
          </div>
          <h1 className="heading text-4xl md:text-5xl font-black uppercase tracking-tighter mb-4">
            $99 a week. Every feature.<br />Cancel any week.
          </h1>
          <p className="text-lg text-zinc-400 max-w-xl mx-auto">
            No tiers, no add-ons, no season-long commitment. Pay for the weeks you play. The field model,
            correlated simulations, late swap and weekly grading are all included from the first build.
          </p>
        </div>

        <div className="max-w-xl mx-auto">
          <PlanCard
            id="weekly" label="Weekly" price="$99" period="/week"
            subtitle="Billed every 7 days. Cancel from Account → Subscription and you keep access through the end of the week you paid for."
            features={[
              "Smart Optimizer — contest-shaped rules for Milly Maker, GPP, Showdown and cash",
              "Field model built from the projection sites the crowd uses",
              "Correlated Monte Carlo with Field-Win % ranking and copies estimate",
              "Manual Optimizer — group rules, exposure caps, peer swap, Field-Win ranking",
              "Pre-lock inactive fix + lock-aware Late Swap re-solve",
              "Weekly self-grading against DraftKings standings",
              "AI slate brief (Claude Sonnet 5)",
              "Unlimited slates and lineups · DK-ready CSV export",
            ]}
            highlight
            onPick={() => pick("weekly")}
            busy={busy === "weekly"}
          />
        </div>

        <div className="mt-12 flex items-center justify-center gap-2 text-xs text-zinc-500 mono">
          <ShieldCheck size={14} className="text-[#39FF14]" weight="fill" />
          Secure checkout by Stripe · Cancel anytime · No hidden fees
        </div>

        <DkNativeFAQ />
      </main>
    </div>
  );
}

const FAQS = [
  {
    q: "Why weekly instead of monthly?",
    a: (
      <>
        Because DFS is played week to week. Byes, travel, a bad bankroll week — you shouldn't be paying
        for Sundays you sit out. <span className="text-white">$99 covers one full slate cycle</span>: build,
        pre-lock scratch fixes, late swap and Monday's grading. Stay subscribed and it simply renews each week;
        cancel and you keep access through the week you paid for.
      </>
    ),
  },
  {
    q: "What do I get that the projection sites don't give me?",
    a: (
      <>
        Their numbers are the input to WarPaint, not the output. Load the projection and ownership exports from
        the sites the field subscribes to and the engine treats that consensus as
        <span className="text-white"> the field itself</span> — estimating which lineups those tools will produce,
        how many copies each will have, and building yours where they aren't. Then every lineup is ranked by
        <span className="text-white"> how often it beats the field in correlated simulation</span>, not by projected points.
      </>
    ),
  },
  {
    q: "Why DraftKings only?",
    a: (
      <>
        Supporting six sites means <span className="text-white">averaging</span> stack rules,
        <span className="text-white"> blending</span> scoring and <span className="text-white">smoothing</span> payout curves
        into one generic engine. WarPaint is written against DraftKings alone — the roster, the bonuses, the Milly Maker
        prize cliff — with separate rulebooks for cash, small-field, large-field and Showdown.
      </>
    ),
  },
  {
    q: "I play small-field GPPs and cash. Is this built for me too?",
    a: (
      <>
        Yes. Rules derive from the contest you enter: a 3-max or single-entry gets tighter correlation, higher floors and
        concentrated exposure; cash runs a <span className="text-white">floor objective</span> on median outcomes with no
        leverage forcing. Same subscription, different rulebooks.
      </>
    ),
  },
  {
    q: "How do I know it's actually working?",
    a: (
      <>
        Upload the DraftKings standings on Monday. The engine grades its <span className="text-white">pre-lock</span> projections
        and ownership against what happened — error by position, chalk misses, which competitor site was sharpest — and
        publishes it on your Portfolio page. Those errors feed back into the model the following week.
      </>
    ),
  },
];

function DkNativeFAQ() {
  return (
    <section className="mt-20 border-t border-zinc-900 pt-16">
      <div className="text-center mb-10">
        <div className="overline mb-3 text-[#39FF14]">Questions</div>
        <h2 className="heading text-3xl md:text-4xl font-black uppercase tracking-tighter mb-4">
          Straight answers.
        </h2>
        <p className="text-sm text-zinc-500 max-w-lg mx-auto">
          Pricing, the field model, and why DraftKings only.
        </p>
      </div>

      <div className="space-y-2">
        {FAQS.map((f, i) => (
          <details
            key={f.q}
            data-testid={`faq-${i}`}
            className="group bg-[#121212] border border-zinc-800 hover:border-zinc-700 open:border-[#39FF14]/50 transition-colors"
          >
            <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4">
              <span className="heading font-bold text-white text-sm md:text-base">{f.q}</span>
              <span className="text-[#39FF14] mono text-xs shrink-0 transition-transform group-open:rotate-45">＋</span>
            </summary>
            <div className="px-5 pb-5 text-sm text-zinc-400 leading-relaxed border-t border-zinc-900 pt-4">
              {f.a}
            </div>
          </details>
        ))}
      </div>

      <div className="mt-10 flex items-center justify-center">
        <div className="border-l-2 border-[#39FF14] bg-[#39FF14]/5 px-5 py-4 max-w-xl">
          <div className="text-sm text-zinc-200 leading-relaxed">
            <span className="mono text-xs tracking-widest uppercase text-[#39FF14] block mb-1">Bottom line</span>
            Built to beat the field, not the projection. $99 a week, every feature, cancel any week.
          </div>
        </div>
      </div>
    </section>
  );
}

function PlanCard({ id, label, price, period, subtitle, features, highlight, onPick, busy }) {
  return (
    <div
      data-testid={`plan-${id}`}
      className={`bg-[#121212] p-8 relative ${highlight ? "border-2 border-[#39FF14] mm-ai-glow" : "border border-zinc-800"}`}
    >
      {highlight && (
        <div className="absolute -top-3 left-6 bg-[#39FF14] text-black px-3 py-1 text-[10px] uppercase tracking-widest font-black">All-Access</div>
      )}
      <div className="overline mb-3">{label}</div>
      <div className="heading text-5xl font-black text-white mb-2">
        {price}<span className="text-lg text-zinc-500">{period}</span>
      </div>
      <div className="text-sm text-zinc-400 mb-6">{subtitle}</div>
      <ul className="space-y-2 text-sm text-zinc-300 mb-8">
        {features.map((f) => (<li key={f}>✓ {f}</li>))}
      </ul>
      <button
        data-testid={`checkout-${id}`}
        onClick={onPick} disabled={busy}
        className={`w-full py-3 font-black uppercase tracking-widest text-xs flex items-center justify-center gap-2 ${
          highlight ? "bg-[#39FF14] text-black hover:bg-[#2ee30d]" : "border border-zinc-700 text-white hover:bg-zinc-900"
        } disabled:opacity-40`}
      >
        {busy ? "Redirecting…" : "Start this week — $99"}
        {!busy && <ArrowRight size={14} weight="bold" />}
      </button>
    </div>
  );
}
